import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {averageOrderValueAmount} from "../../utils/reportingMetricRatios.js";
import ReportingSnapshotStatus from "./ReportingSnapshotStatus.jsx";
import {isSnapshotPeriodIncomplete, snapshotDateValue, snapshotHasPublishedDays, snapshotPeriodValue, withSnapshotMissingDays} from "../../utils/reportingSnapshotAvailability.js";
import {
    currencyUnit,
    dividePercent,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatChartPercent,
    formatDateLabel,
    KpiCard,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
} from "./ReportingLab.jsx";

function granularDates(data) {
    return [...new Set([
        ...Object.keys(data?.orderAmountGrossGranular ?? {}),
        ...Object.keys(data?.orderAmountNetGranular ?? {}),
        ...Object.keys(data?.orderAmountReceivedGranular ?? {}),
        ...Object.keys(data?.orderAmountCompletedGranular ?? {}),
        ...Object.keys(data?.orderAmountCancelledGranular ?? {}),
        ...Object.keys(data?.orderTurnoverGrossGranular ?? {}),
        ...Object.keys(data?.orderTurnoverNetGranular ?? {}),
        ...Object.keys(data?.orderTurnoverCancelledGranular ?? {}),
    ])].sort();
}

function granularMoneyMajorForSnapshot(granular, date, snapshot) {
    const value = snapshotDateValue(granular?.[date], snapshot, date);
    return value == null ? null : moneyToMajor(value);
}

function granularCount(granular, date) {
    return Number(granular?.[date] ?? 0);
}

export function cancelledTurnoverHasUnavailableSourceReason(turnover, reason) {
    return turnover == null && reason === "zero_header_with_retained_fees";
}

export function cancelledTurnoverUnavailableTooltip(turnover, reason, t) {
    return cancelledTurnoverHasUnavailableSourceReason(turnover, reason)
        ? t("reporting_lab.orders_lab.cancelled_turnover_unavailable_tooltip", {defaultValue: "U stornovaných objednávek je zdrojová hlavičková částka nulová, ale zůstaly nenulové poplatky za dopravu nebo platbu. Obrat zboží proto nelze určit; nedostupné jsou i AOV a denní hodnota."})
        : null;
}

function requestedDates(since, until) {
    const dates = [];
    const cursor = since.clone();
    const end = until.format("YYYY-MM-DD");
    while (cursor.format("YYYY-MM-DD") <= end) {
        dates.push(cursor.format("YYYY-MM-DD"));
        cursor.add(1, "day");
    }
    return dates;
}

export function calculateStatusRateSeries(dates, data, snapshot = undefined) {
    const completion = [];
    const cancellation = [];

    for (const date of dates) {
        const received = snapshotDateValue(granularCount(data?.orderAmountReceivedGranular, date), snapshot, date);
        const completed = snapshotDateValue(granularCount(data?.orderAmountCompletedGranular, date), snapshot, date);
        const cancelled = snapshotDateValue(granularCount(data?.orderAmountCancelledGranular, date), snapshot, date);
        if (received == null || completed == null || cancelled == null) {
            completion.push(null);
            cancellation.push(null);
            continue;
        }
        const total = received + completed + cancelled;

        completion.push(dividePercent(completed, total));
        cancellation.push(dividePercent(cancelled, total));
    }

    return {completion, cancellation};
}

export default function ReportingOrdersLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setData(null);

        new ErpCalculationApi(getAuthorityConfiguration())
            .shopPerformance({projectId: project.id, erpFilter})
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setData(nextData);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, erpFilter]);

    const snapshot = data?.snapshot;
    const snapshotIncomplete = isSnapshotPeriodIncomplete(snapshot);
    const summaryUnavailable = !snapshotHasPublishedDays(snapshot);
    const dates = useMemo(() => snapshotIncomplete
        ? withSnapshotMissingDays(snapshot, requestedDates(since, until))
        : granularDates(data), [data, snapshot, snapshotIncomplete, sinceString, untilString]);
    const currency = data?.currency
        ?? data?.orderTurnoverGross?.currency
        ?? data?.orderTurnoverNet?.currency
        ?? projectCurrency(project);

    const received = snapshotPeriodValue(data?.orderAmountReceived, snapshot);
    const completed = snapshotPeriodValue(data?.orderAmountCompleted, snapshot);
    const cancelled = snapshotPeriodValue(data?.orderAmountCancelled, snapshot);
    const marketingOrders = snapshotPeriodValue(data?.orderAmountGross, snapshot);
    const businessOrders = snapshotPeriodValue(data?.orderAmountNet, snapshot);
    const cancellationRate = received == null || completed == null || cancelled == null ? null : dividePercent(cancelled, received + completed + cancelled);
    const completionRate = received == null || completed == null || cancelled == null ? null : dividePercent(completed, received + completed + cancelled);
    const statusRateSeries = calculateStatusRateSeries(dates, data, snapshot);
    const cancelledTurnover = snapshotPeriodValue(data?.orderTurnoverCancelled ?? null, snapshot);
    const cancelledAverage = cancelledTurnover == null || cancelled == null ? null : averageOrderValueAmount(cancelledTurnover?.amount, cancelled);
    const cancelledTurnoverUnavailableInfo = cancelledTurnoverUnavailableTooltip(
        cancelledTurnover,
        data?.cancelledTurnoverUnavailableReason,
        t,
    );
    const pendingTurnover = summaryUnavailable ? null : Math.max(0, moneyAmount(data?.orderTurnoverGross) - moneyAmount(data?.orderTurnoverNet));
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const statusTrendOption = {
        color: ["#8b5cf6", "#16a34a", "#ef4444"],
        textStyle: chartTextStyle,
        grid: {left: 18, right: 18, top: 26, bottom: 48, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, formatChartNumber),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: dates.map(formatDateLabel),
            axisLabel: {fontSize: 12},
        },
        yAxis: {
            type: "value",
            minInterval: 1,
            name: t("reporting_lab.orders_lab.orders_axis", {defaultValue: "Obj."}),
            nameTextStyle: chartTextStyle,
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        series: [
            {
                name: t("reporting_lab.orders.received", {defaultValue: "Přijaté"}),
                type: "bar",
                stack: "orders",
                data: dates.map((date) => snapshotDateValue(granularCount(data?.orderAmountReceivedGranular, date), snapshot, date)),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.orders.completed", {defaultValue: "Dokončené"}),
                type: "bar",
                stack: "orders",
                data: dates.map((date) => snapshotDateValue(granularCount(data?.orderAmountCompletedGranular, date), snapshot, date)),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.orders.cancelled", {defaultValue: "Stornované"}),
                type: "bar",
                stack: "orders",
                data: dates.map((date) => snapshotDateValue(granularCount(data?.orderAmountCancelledGranular, date), snapshot, date)),
                barMaxWidth: 28,
            },
        ],
    };

    const basisTrendOption = {
        color: ["#2563eb", "#16a34a", "#7c3aed", "#f97316"],
        textStyle: chartTextStyle,
        grid: {left: 18, right: 56, top: 28, bottom: 48, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, [
                (value) => formatChartCurrency(value, currency),
                (value) => formatChartCurrency(value, currency),
                formatChartNumber,
                formatChartNumber,
            ]),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: dates.map(formatDateLabel),
            axisLabel: {fontSize: 12},
        },
        yAxis: [
            {
                type: "value",
                name: currencyUnit(currency),
                nameTextStyle: chartTextStyle,
                axisLabel: {fontSize: 12, formatter: formatChartNumber},
            },
            {
                type: "value",
                name: t("reporting_lab.orders_lab.orders_axis", {defaultValue: "Obj."}),
                nameTextStyle: chartTextStyle,
                axisLabel: {fontSize: 12, formatter: formatChartNumber},
            },
        ],
        series: [
            {
                name: t("reporting_lab.orders_lab.marketing_turnover", {defaultValue: "Obrat objednávek bez storen"}),
                type: "bar",
                data: dates.map((date) => granularMoneyMajorForSnapshot(data?.orderTurnoverGrossGranular, date, snapshot)),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.orders_lab.business_turnover", {defaultValue: "Obrat dokončených objednávek"}),
                type: "bar",
                data: dates.map((date) => granularMoneyMajorForSnapshot(data?.orderTurnoverNetGranular, date, snapshot)),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.metric_basis.marketing.label", {defaultValue: "Marketingový výkon"}),
                type: "line",
                yAxisIndex: 1,
                smooth: true,
                data: dates.map((date) => snapshotDateValue(granularCount(data?.orderAmountGrossGranular, date), snapshot, date)),
            },
            {
                name: t("reporting_lab.metric_basis.business.label", {defaultValue: "Obchodní výkon"}),
                type: "line",
                yAxisIndex: 1,
                smooth: true,
                data: dates.map((date) => snapshotDateValue(granularCount(data?.orderAmountNetGranular, date), snapshot, date)),
            },
        ],
    };

    const rateTrendOption = {
        color: ["#16a34a", "#ef4444"],
        textStyle: chartTextStyle,
        grid: {left: 18, right: 18, top: 24, bottom: 48, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartPercent(value, 1)),
        },
        legend: {bottom: 0, left: "center", ...chartLegendStyle},
        xAxis: {
            type: "category",
            data: dates.map(formatDateLabel),
            axisLabel: {fontSize: 12},
        },
        yAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: (value) => formatChartPercent(value)},
        },
        series: [
            {
                name: t("reporting_lab.orders_lab.completion_rate", {defaultValue: "Míra dokončení"}),
                type: "line",
                smooth: true,
                data: statusRateSeries.completion,
            },
            {
                name: t("reporting_lab.orders_lab.cancellation_rate", {defaultValue: "Míra storen"}),
                type: "line",
                smooth: true,
                data: statusRateSeries.cancellation,
            },
        ],
    };

    const dailyRows = dates.map((date) => {
        const rowReceived = snapshotDateValue(granularCount(data?.orderAmountReceivedGranular, date), snapshot, date);
        const rowCompleted = snapshotDateValue(granularCount(data?.orderAmountCompletedGranular, date), snapshot, date);
        const rowCancelled = snapshotDateValue(granularCount(data?.orderAmountCancelledGranular, date), snapshot, date);
        const rowTotal = rowReceived + rowCompleted + rowCancelled;

        return {
            date,
            received: rowReceived,
            completed: rowCompleted,
            cancelled: rowCancelled,
            marketingOrders: snapshotDateValue(granularCount(data?.orderAmountGrossGranular, date), snapshot, date),
            businessOrders: snapshotDateValue(granularCount(data?.orderAmountNetGranular, date), snapshot, date),
            turnoverMarketing: snapshotDateValue(data?.orderTurnoverGrossGranular?.[date], snapshot, date),
            turnoverBusiness: snapshotDateValue(data?.orderTurnoverNetGranular?.[date], snapshot, date),
            cancellationRate: rowReceived == null || rowCompleted == null || rowCancelled == null ? null : dividePercent(rowCancelled, rowTotal),
            turnoverCancelled: snapshotDateValue(data?.orderTurnoverCancelledGranular?.[date], snapshot, date),
        };
    }).reverse();

    return <WideSecureLayout periodReport retentionExpired={retentionExpired}
        title={t("reporting_lab.pages.orders", {defaultValue: "Objednávky a storna"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
     intro={<ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.orders.title", {defaultValue: "K čemu slouží objednávky a storna"})}
                >
                    {t("reporting_lab.descriptions.orders.text", {defaultValue: "Porovnává obrat všech objednávek kromě stornovaných s obratem dokončených objednávek a ukazuje rozpad přijatých, dokončených a stornovaných objednávek."})}
                </ReportingPageDescription>}>
        <div className="grid grid-cols-12 gap-4">
                <ReportingSnapshotStatus snapshots={snapshot}/>

                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.orders_lab.marketing_basis", {defaultValue: "Objednávky bez storen"})} value={marketingOrders == null ? "—" : renderNumber(marketingOrders)}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.orders_lab.business_basis", {defaultValue: "Dokončené objednávky"})} value={businessOrders == null ? "—" : renderNumber(businessOrders)} tone="good"/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.orders_lab.received_total", {defaultValue: "Přijaté (nedokončené)"})} value={received == null ? "—" : renderNumber(received)}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.orders.cancelled", {defaultValue: "Stornované"})} value={cancelled == null ? "—" : renderNumber(cancelled)} meta={`${t("reporting_lab.orders_lab.cancellation_rate", {defaultValue: "Míra storen"})}: ${renderPercentValue(cancellationRate)}`}/>
                </div>

                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.orders_lab.cancelled_turnover", {defaultValue: "Stornovaný obrat"})} value={cancelledTurnover == null ? "—" : renderCurrencyValue(cancelledTurnover, true)} meta={t("reporting_lab.orders_lab.cancelled_turnover_meta", {defaultValue: "Hodnota objednávek označených jako stornované"})} info={cancelledTurnoverUnavailableInfo} infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}/>
                </div>
                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.orders_lab.cancelled_aov", {defaultValue: "AOV stornovaných"})} value={cancelledAverage === null ? "-" : renderCurrencyValue({amount: cancelledAverage, currency}, true)} meta={t("reporting_lab.orders_lab.cancelled_aov_meta", {defaultValue: "Průměrná hodnota stornované objednávky"})}/>
                </div>
                <div className="col-span-12 md:col-span-4">
                    <KpiCard loading={loading} label={t("reporting_lab.orders_lab.pending_turnover", {defaultValue: "Zatím nedokončený obrat"})} value={pendingTurnover == null ? "—" : renderCurrencyValue({amount: pendingTurnover, currency}, true)} meta={t("reporting_lab.orders_lab.pending_turnover_meta", {defaultValue: "Rozdíl obratu všech objednávek bez storen a dokončených"})}/>
                </div>

                <Panel loading={loading} title={t("reporting_lab.orders_lab.status_trend", {defaultValue: "Stav objednávek v čase"})} className="col-span-12 xl:col-span-6">
                    <EChart option={statusTrendOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.orders_lab.basis_trend", {defaultValue: "Objednávky bez storen a dokončené objednávky"})} className="col-span-12 xl:col-span-6">
                    <EChart option={basisTrendOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.orders_lab.rate_trend", {defaultValue: "Dokončení a storna v čase"})} className="col-span-12 xl:col-span-5">
                    <EChart option={rateTrendOption} className="h-80"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.orders_lab.basis_explanation", {defaultValue: "Jak se liší počty objednávek"})} className="col-span-12 xl:col-span-7">
                    <div className="grid gap-3 md:grid-cols-2">
                        <div className="app-surface rounded-lg border border-violet-200 bg-violet-50/40 p-4 dark:border-violet-900 dark:bg-violet-950/20">
                            <div className="text-sm font-bold text-violet-700 dark:text-violet-200">{t("reporting_lab.orders_lab.marketing_turnover_short", {defaultValue: "Obrat bez storen"})}</div>
                            <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">{t("reporting_lab.metric_basis.marketing.description", {defaultValue: "Všechny objednávky kromě stornovaných, včetně objednávek bez známého stavu."})}</div>
                            <div className="mt-3 text-2xl font-bold text-slate-900 dark:text-slate-100">{summaryUnavailable ? "—" : renderCurrencyValue(data?.orderTurnoverGross, true)}</div>
                        </div>
                        <div className="app-surface rounded-lg border border-emerald-200 bg-emerald-50/40 p-4 dark:border-emerald-900 dark:bg-emerald-950/20">
                            <div className="text-sm font-bold text-emerald-700 dark:text-emerald-200">{t("reporting_lab.orders_lab.business_turnover_short", {defaultValue: "Obrat dokončených"})}</div>
                            <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">{t("reporting_lab.metric_basis.business.description", {defaultValue: "Zahrnuje pouze dokončené objednávky podle mapování stavů v projektu; samo o sobě nepotvrzuje vystavení faktury."})}</div>
                            <div className="mt-3 text-2xl font-bold text-slate-900 dark:text-slate-100">{summaryUnavailable ? "—" : renderCurrencyValue(data?.orderTurnoverNet, true)}</div>
                        </div>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                        <KpiCard loading={loading} label={t("reporting_lab.orders_lab.completion_rate", {defaultValue: "Míra dokončení"})} value={renderPercentValue(completionRate)}/>
                        <KpiCard loading={loading} label={t("reporting_lab.orders_lab.cancellation_rate", {defaultValue: "Míra storen"})} value={renderPercentValue(cancellationRate)}/>
                        <KpiCard loading={loading} label={t("reporting_lab.orders_lab.marketing_aov", {defaultValue: "AOV bez storen"})} value={summaryUnavailable ? "—" : renderCurrencyValue(data?.orderAverageGross, true)}/>
                        <KpiCard loading={loading} label={t("reporting_lab.orders_lab.business_aov", {defaultValue: "AOV dokončených"})} value={summaryUnavailable ? "—" : renderCurrencyValue(data?.orderAverageNet, true)}/>
                    </div>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.orders_lab.daily_table", {defaultValue: "Denní přehled objednávek"})} className="col-span-12">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.table.date", {defaultValue: "Datum"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.orders.received", {defaultValue: "Přijaté"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.orders.completed", {defaultValue: "Dokončené"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.orders.cancelled", {defaultValue: "Stornované"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.orders_lab.marketing_basis", {defaultValue: "Objednávky bez storen"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.orders_lab.business_basis", {defaultValue: "Dokončené objednávky"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.orders_lab.marketing_turnover_short", {defaultValue: "Obrat MKT"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.orders_lab.business_turnover_short", {defaultValue: "Obrat obchod"})}</th>
                                <th className="py-2 text-right font-semibold">{t("reporting_lab.orders_lab.cancellation_rate", {defaultValue: "Míra storen"})}</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {dailyRows.map((row) => <tr key={row.date}>
                                <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{formatDateLabel(row.date)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{row.received == null ? "—" : renderNumber(row.received)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{row.completed == null ? "—" : renderNumber(row.completed)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{row.cancelled == null ? "—" : renderNumber(row.cancelled)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{row.marketingOrders == null ? "—" : renderNumber(row.marketingOrders)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{row.businessOrders == null ? "—" : renderNumber(row.businessOrders)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.turnoverMarketing, true)}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.turnoverBusiness, true)}</td>
                                <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(row.cancellationRate)}</td>
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            </div>
    </WideSecureLayout>;
}
